import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { api, resolveAssetUrl, type AdminCosmetic, type Rarity } from '@/api/client';
import { ErrorBox, Field, Loading, Modal, PageTitle } from '@/components/ui';

const RARITY_TINT: Record<Rarity, string> = {
  common: 'var(--ink-muted)',
  rare: 'var(--c-blue)',
  epic: 'var(--c-purple)',
  legendary: 'var(--c-yellow)',
};

/** Catalog of finisher FX keys the client knows how to render. Mirror of
 * apps/client/src/game/fx/finishers.ts → FINISHERS / FINISHER_META. Keep in
 * sync when a new finisher is implemented client-side. */
const FX_KEYS: Array<{ key: string; emoji: string; label: string }> = [
  { key: 'default', emoji: '🥾', label: 'Victory Stomp' },
  { key: 'confetti_storm', emoji: '🎉', label: 'Confetti Storm' },
  { key: 'coin_rain', emoji: '💰', label: 'Coin Rain' },
  { key: 'lightning', emoji: '⚡', label: 'Lightning Strike' },
  { key: 'ice_shatter', emoji: '❄️', label: 'Ice Shatter' },
  { key: 'meteor', emoji: '☄️', label: 'Meteor Smash' },
  { key: 'black_hole', emoji: '🕳️', label: 'Black Hole' },
  { key: 'ko_flash', emoji: '🥊', label: 'K.O. Flash' },
  { key: 'dragon_breath', emoji: '🐉', label: 'Dragon Breath' },
  { key: 'galaxy', emoji: '🌌', label: 'Galaxy Collapse' },
  { key: 'earthquake', emoji: '🌋', label: 'Earthquake' },
];

function emojiFor(fxKey: string | undefined): string {
  return FX_KEYS.find((f) => f.key === fxKey)?.emoji ?? '💥';
}

export function FinishersPage() {
  const [items, setItems] = useState<AdminCosmetic[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminCosmetic | 'new' | null>(null);

  const load = () =>
    api
      .cosmetics()
      .then((r) => setItems(r.items.filter((c) => c.slot === 'endgameFx')))
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    void load();
  }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  return (
    <div>
      <PageTitle
        title="Finishers"
        action={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            + NEW FINISHER
          </button>
        }
      />

      <div className="tag" style={{ marginBottom: 8 }}>
        ENDGAME FINISHERS (the win-FX players buy &amp; equip)
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 64 }}>FX</th>
              <th>Name</th>
              <th>Rarity</th>
              <th style={{ textAlign: 'right' }}>Coins</th>
              <th style={{ textAlign: 'right' }}>Gems</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const fxKey = c.data?.fxKey as string | undefined;
              const hasClip = !!c.data?.mediaUrl;
              return (
                <tr key={c.id} onClick={() => setEditing(c)}>
                  <td style={{ fontSize: 26, textAlign: 'center' }}>
                    {hasClip ? '🎬' : emojiFor(fxKey)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ color: 'var(--ink-dim)', fontSize: 10, fontFamily: 'var(--f-mono)', marginTop: 2 }}>
                      {hasClip ? `🎬 video clip (fallback: ${fxKey ?? '—'})` : (fxKey ?? '—')}
                    </div>
                    {c.description && (
                      <div style={{ color: 'var(--ink-muted)', fontSize: 11, marginTop: 2 }}>
                        {c.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="tag" style={{ color: RARITY_TINT[c.rarity], fontSize: 9 }}>
                      {c.rarity.toUpperCase()}
                    </span>
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>{c.priceCoins.toLocaleString()}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{c.priceGems.toLocaleString()}</td>
                  <td>
                    {c.isStarter && (
                      <span className="badge badge-guest" style={{ marginRight: 4 }}>STARTER</span>
                    )}
                    {!c.isActive && <span className="badge badge-ban">HIDDEN</span>}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr style={{ cursor: 'default' }}>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>
                  No finishers. Click “+ NEW FINISHER” to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <FinisherEdit
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function FinisherEdit({
  initial,
  onClose,
  onDone,
}: {
  initial: AdminCosmetic | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [fxKey, setFxKey] = useState((initial?.data?.fxKey as string | undefined) ?? 'confetti_storm');
  const [mediaUrl, setMediaUrl] = useState((initial?.data?.mediaUrl as string | undefined) ?? '');
  const [keyType, setKeyType] = useState((initial?.data?.keyType as string | undefined) ?? 'green');
  const [durationSec, setDurationSec] = useState(Number(initial?.data?.durationSec) || 0);
  const [cutWatermark, setCutWatermark] = useState(!!initial?.data?.cutWatermark);
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rarity, setRarity] = useState<Rarity>(initial?.rarity ?? 'rare');
  const [priceCoins, setPriceCoins] = useState(initial?.priceCoins ?? 0);
  const [priceGems, setPriceGems] = useState(initial?.priceGems ?? 0);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [isStarter, setIsStarter] = useState(initial?.isStarter ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) {
      setErr('Name is required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        slot: 'endgameFx',
        name: name.trim(),
        description: description || null,
        rarity,
        priceCoins,
        priceGems,
        iconUrl: null,
        // Single plain MP4 (green/black background). The client keys it LIVE
        // with a WebGL shader → transparent over the game on iOS + Android.
        // fxKey is the procedural fallback when no clip is set.
        data: {
          fxKey,
          ...(mediaUrl ? { mediaUrl, keyType, ...(cutWatermark ? { cutWatermark: true } : {}) } : {}),
          // Max playback length in seconds; 0/empty = play the full clip.
          ...(durationSec > 0 ? { durationSec } : {}),
        },
        isActive,
        isStarter,
      } as const;
      if (initial) await api.patchCosmetic(initial.id, payload);
      else await api.createCosmetic(payload);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!initial) return;
    if (!confirm(`Delete finisher "${initial.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteCosmetic(initial.id);
      onDone();
    } catch (e) {
      setErr(String(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={initial ? `Edit · ${initial.name}` : 'New finisher'}
      onClose={onClose}
      footer={
        <>
          {initial && (
            <button className="btn btn-danger" onClick={remove} disabled={busy}>
              Delete
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : initial ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      {!mediaUrl && (
        <div style={{ display: 'flex', justifyContent: 'center', fontSize: 56, padding: '4px 0 8px' }}>
          {emojiFor(fxKey)}
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginBottom: 6 }}>
        Finisher clip — a plain <b>MP4</b> shot on a solid <b>GREEN</b> or
        <b> BLACK</b> background. The game removes the background LIVE (WebGL key)
        so it’s transparent over the match on iOS + Android. No pre-keying needed
        — upload the raw clip. Leave empty to use the procedural fallback FX.
      </div>
      <MediaUploader
        label="FINISHER CLIP (.mp4)"
        hint="Plain H.264 MP4, ≤ 20 MB. Green screen for solid subjects (dragon, meteor…), black for glow FX (lightning, fire…)."
        accept="video/mp4,.mp4"
        url={mediaUrl}
        onChange={setMediaUrl}
      />
      {mediaUrl && (
        <Field
          label="BACKGROUND TO REMOVE"
          hint="Green = chroma key (solid subjects). Black = luma key (bright glow FX)."
        >
          <select value={keyType} onChange={(e) => setKeyType(e.target.value)}>
            <option value="green">🟢 Green screen (chroma)</option>
            <option value="black">⬛ Black (luma / glow)</option>
          </select>
        </Field>
      )}
      {mediaUrl && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '2px 0 8px' }}>
          <input type="checkbox" checked={cutWatermark} onChange={(e) => setCutWatermark(e.target.checked)} />
          Cut bottom watermark (hides a baked-in logo strip, e.g. KlingAI)
        </label>
      )}

      <Field
        label="CLIP DURATION (seconds)"
        hint="How long the clip plays before the result screen. 0 = play the full clip (natural length). Set a smaller number to cut a long clip short."
      >
        <input
          type="number"
          min={0}
          step={0.5}
          value={durationSec}
          onChange={(e) => setDurationSec(Math.max(0, +e.target.value || 0))}
        />
      </Field>

      <Field
        label="FALLBACK EFFECT (fxKey)"
        hint="Procedural FX used when no media clip is uploaded. Must match an implemented finisher."
      >
        <select value={fxKey} onChange={(e) => setFxKey(e.target.value)}>
          {FX_KEYS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.emoji} {f.label} ({f.key})
            </option>
          ))}
        </select>
      </Field>
      <Field label="NAME">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="RARITY">
        <select value={rarity} onChange={(e) => setRarity(e.target.value as Rarity)}>
          <option value="common">common</option>
          <option value="rare">rare</option>
          <option value="epic">epic</option>
          <option value="legendary">legendary</option>
        </select>
      </Field>
      <Field label="DESCRIPTION">
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="PRICE (COINS)">
          <input type="number" value={priceCoins} onChange={(e) => setPriceCoins(+e.target.value || 0)} />
        </Field>
        <Field label="PRICE (GEMS)">
          <input type="number" value={priceGems} onChange={(e) => setPriceGems(+e.target.value || 0)} />
        </Field>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active in shop
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6 }}>
        <input type="checkbox" checked={isStarter} onChange={(e) => setIsStarter(e.target.checked)} />
        Starter (granted free &amp; auto-equipped to new players)
      </label>

      {err && <ErrorBox text={err} />}
    </Modal>
  );
}

/** Upload one finisher clip file (alpha .mov or .webm). Returns the public URL
 * via onChange. Checkerboard preview shows transparency; .mov may not preview in
 * Chrome (no HEVC decode) — the file still uploads fine. */
function MediaUploader({
  label,
  hint,
  accept,
  url,
  onChange,
}: {
  label: string;
  hint: string;
  accept: string;
  url: string;
  onChange: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const preview = resolveAssetUrl(url);

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await api.uploadFinisherMedia(file);
      onChange(res.url);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Field label={label} hint={hint}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 64,
            height: 114,
            borderRadius: 10,
            background:
              'repeating-conic-gradient(rgba(255,255,255,0.06) 0% 25%, transparent 0% 50%) 50% / 12px 12px, rgba(0,0,0,0.2)',
            border: '1px solid var(--hairline)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {preview ? (
            <video
              src={preview}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              muted
              loop
              autoPlay
              playsInline
            />
          ) : (
            <span style={{ color: 'var(--ink-dim)', fontSize: 9, textAlign: 'center' }}>NO<br />CLIP</span>
          )}
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={onPick}
            disabled={busy}
            style={{ fontSize: 11 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={url}
              onChange={(e) => onChange(e.target.value)}
              placeholder="/uploads/cosmetics/… or external URL"
              style={{ width: '100%', fontSize: 11, fontFamily: 'var(--f-mono)' }}
            />
            {url && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => onChange('')}
                style={{ fontSize: 10, padding: '4px 8px' }}
                title="Clear clip"
              >
                CLEAR
              </button>
            )}
          </div>
          {busy && <span style={{ fontSize: 11, color: 'var(--c-cyan)' }}>Uploading…</span>}
          {err && <ErrorBox text={err} />}
        </div>
      </div>
    </Field>
  );
}
